// ===========================================
// AUTH CALLBACK - Platform Hub / Cognito / Social
// ===========================================

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../../components/ui';
import { useNotify, useAuthStore } from '../../store';
import { getApiBaseUrl } from '../../lib/apiBase';
import { sanitizeHubReturnPath } from '../../lib/hubReturnPath';

const API_BASE_URL = getApiBaseUrl();

function apiUrl(path: string) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${p.startsWith('/api') ? p : `/api${p}`}`;
}

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { setUser } = useAuthStore();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      try {
        const accessToken =
          searchParams.get('accessToken') ||
          searchParams.get('access_token') ||
          searchParams.get('token');
        const refreshToken =
          searchParams.get('refreshToken') ||
          searchParams.get('refresh_token') ||
          searchParams.get('refresh');
        const sessionId = searchParams.get('sessionId');
        const provider = (searchParams.get('provider') || '').toLowerCase();
        const error = searchParams.get('error');

        if (error) {
          throw new Error(decodeURIComponent(error));
        }

        if (!accessToken) {
          throw new Error('Missing authentication token');
        }

        const isHub =
          provider === 'hub' ||
          provider === 'platform_hub' ||
          searchParams.get('source') === 'hub' ||
          searchParams.get('source') === 'platform-hub';

        let salesAccess = accessToken;
        let salesRefresh = refreshToken || '';
        let user: any = null;

        if (isHub) {
          // GET /auth/sso already exchanged Hub→Sales JWTs when refreshToken is present.
          // Only call /api/v1/sso/validate when we still have a raw Hub launch token.
          const alreadyExchanged = Boolean(refreshToken);
          if (!alreadyExchanged) {
            const exchangeRes = await fetch(apiUrl('/api/v1/sso/validate'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: accessToken, accessToken }),
            });
            const exchangeData = await exchangeRes.json();
            if (!exchangeRes.ok || !exchangeData.success) {
              throw new Error(exchangeData.error || 'Platform Hub SSO exchange failed');
            }
            salesAccess = exchangeData.data.tokens.accessToken;
            salesRefresh = exchangeData.data.tokens.refreshToken;
            user = exchangeData.data.user;
          }
        }

        if (!user) {
          // Cognito/social or Hub path after exchange — load profile via /me
          const profileResponse = await fetch(apiUrl('/api/auth/me'), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${salesAccess}`,
            },
          });

          if (!profileResponse.ok) {
            // Token may be a Hub token without provider flag — try exchange
            const exchangeRes = await fetch(apiUrl('/api/v1/sso/validate'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: accessToken, accessToken }),
            });
            const exchangeData = await exchangeRes.json();
            if (!exchangeRes.ok || !exchangeData.success) {
              throw new Error('Failed to establish Sales session');
            }
            salesAccess = exchangeData.data.tokens.accessToken;
            salesRefresh = exchangeData.data.tokens.refreshToken;
            user = exchangeData.data.user;
          } else {
            const profileData = await profileResponse.json();
            user = profileData.data?.user || profileData.user;
          }
        }

        if (!user) throw new Error('Failed to get user profile');
        if (!salesRefresh) throw new Error('Missing refresh token');

        localStorage.setItem('access_token', salesAccess);
        localStorage.setItem('refresh_token', salesRefresh);
        if (sessionId) localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);

        setStatus('success');
        notify.success('Welcome Back!', `Good to see you, ${user.firstName || 'there'}!`);
        const next = sanitizeHubReturnPath(
          searchParams.get('redirect') || searchParams.get('returnPath') || '/dashboard',
          '/dashboard'
        );
        setTimeout(() => navigate(next, { replace: true }), 1000);
      } catch (err) {
        console.error('[AuthCallback] Error:', err);
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Authentication failed');
        notify.error('Login Failed', err instanceof Error ? err.message : 'Unexpected error');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    processCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-12 text-center">
        {status === 'processing' && (
          <>
            <div className="mb-6">
              <LoadingSpinner size="lg" variant="plane" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Authenticating...</h1>
            <p className="text-gray-600">Please wait while we verify your credentials</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Login Successful!</h1>
            <p className="text-gray-600">Redirecting to your dashboard...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Failed</h1>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <p className="text-sm text-gray-500">Redirecting to login page...</p>
          </>
        )}
      </div>
    </div>
  );
}
